/*
* Navigation link helper functions
*/

import * as PropTypes from 'prop-types';
import * as React from 'react';
import RouteParser from 'route-parser';
import * as _ from 'lodash';
import { Link } from 'react-router-dom';

import { Formatters } from './formatters';
import Auth from '../auth/auth';
import { Errors } from './errors';

export class NavigationLinks {
    public static ModuleName: string = 'NavigationLinks';

    public static readonly AppBasePath: string = '/app';
    public static readonly AppAuthBasePath: string = `${NavigationLinks.AppBasePath}/auth`;

    public static readonly AppAuthRoutePaths = {
        HomeDashboard: '/home',

        /* Association routes */
        Association: {
            Search: '/association-search',
            ExportOrders: '/association-export-orders',
            Add: '/association-add',
            Detail: '/association/:associationId',
            Edit: '/association/:associationId/edit',
            Inventory: '/association/:associationId/inventory',
            InventorySubgroup: '/association/:associationId/inventory/subgroup/:subgroupCode',
            InventoryDetail: '/association/:associationId/inventory/:inventoryId',
            ReserveStudy: '/association/:associationId/reserve-study',
            PropertyReportPdf: '/association/:associationId/property-report/pdf',
            ClientQuestionsReportPdf: '/association/:associationId/client-questions-report/pdf',
            ViewOrder: '/association/:associationId/view-order',
        },

        /* Client Routes */
        Client: {
            Detail: '/client/:clientId',
            Edit: '/client/:clientId/edit',
            Search: '/client-search',
            SearchContacts: '/client-search-contacts',
            Add: '/client-add',
        },

        ClientDashboard: {
            Home: '/client-dashboard'
        },

        /* Order routes */
        Order: {
            Add: '/order-add',
            AddBid: '/order-add-bid',
            Search: '/order-search',
            Detail: '/order/:orderId',
            Edit: '/order/:orderId/edit',
            BidLetterPdf: '/order/:orderId/bid-letter/pdf',
            BidEnvelopePdf: '/order/:orderId/bid-envelope/pdf',
        },

        PrintMulti: {
            BidEnvelopeMuliplePdf: '/print-multi/bid-envelopes/:orderObjIds/pdf',
            BidLetterMuliplePdf: '/print-multi/bid-letters/:orderObjIds/pdf',
        },

        /* Component routes */
        Component: {
            Detail: '/component/:componentObjId',
            Edit: '/component/:componentObjId/edit',
        },

        /* Bid routes */
        Bids: {
            // Add: '/bid-add',
            Outstanding: '/outstanding-bids',
            Reminder: '/reminder-bids',
            Research: '/research-bids',
            NewResearch: '/new-research-bids',
            PreparedBids: '/prepared-bids',
            SendBids: '/send-bids',
        },

        /* Job routes */
        Jobs: {
            Won: '/won-jobs',
            Coordinator: '/coordinator-jobs-search',
        },

        /* Inventory/Component routes */
        ResearchComponents: {
            Search: '/research-components-search',
        },

        /* Report routes */
        Reports: {
            ArchivedChart: '/association/:associationId/reserve-study/:archivedOrderId',
            ReserveStudyPdf: '/report/association/:associationId/pdf',
            ReserveStudy: '/report/association/:associationId',
            PortalReserveStudy: '/portal/association/:associationId/report',
            ClientQuestionsPdf: '/report/association/:associationId/client-questions/pdf',
            CaliforniaDisclosurePdf: '/report/association/:associationId/california-disclosure/pdf',
        },

        /* User routes */
        Users: {
            Search: '/user-search',
            Detail: '/user/:userObjId',
            Edit: '/user/:userObjId/edit',
            Add: '/user-add',
            AddResume: '/user-add-resume',
        },

        /* User inventory template routes */
        UserInventoryTemplates: {
            Edit: '/user-inventory-template/:userInventoryTemplateObjId/edit',
        },

        /* Invite routes */
        Invite: {
            Add: '/invite/add',
            AddForAssociation: '/invite/add/:associationId',
            Detail: '/invite/:inviteId/detail',
        },

        /* Account routes */
        Account: {
            MyAccount: '/my-account',
        },

        /* Administration routes */
        Administration: {
            DefaultManagers: '/default-managers',
            RegionEdit: '/region/:regionId/edit',
            TextContentEdit: '/text-content/:textContentObjId/edit',
            LookupTables: '/lookup-tables',
            LookupTableEdit: '/lookup-tables/:tableName/edit',
            LookupTableEditChildObj: '/lookup-tables/:tableName/edit/:childObjId',
            QueryDB: '/admin/querydb',
        },

        /* Account activation */
        AccountActivation: {
            ActivateAccount: '/activate/:userObjId',
        },

        /* User change password */
        ChangePassword: {
            ChangePassword: '/change-password/:userObjId',
        },

        /* Auth logout */
        Logout: {
            Logout: '/logout',
        }
    };

    /* CORE HELPERS */
    public static getAppAuthPath = (path: string) => `${NavigationLinks.AppAuthBasePath}${path}`;
    public static getAppPublicPath = (path: string) => `${path}`; // `${NavigationLinks.AppBasePath}${path}`;

    public static composePublicRoutePath(path: string, tokenMap: any = {}) {
        // Returns a composed route path (prepends /app/)
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'composePublicRoutePath', 'path', path);

        const routePath = NavigationLinks.getAppPublicPath(path);
        return new RouteParser(routePath).reverse(tokenMap).toString();
    }

    public static composeAuthenticatedRoutePath(path: string, tokenMap: any = {}) {
        // Returns a composed route path (prepends /app/auth)
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'composeRoutePath', 'path', path);

        const routePath = NavigationLinks.getAppAuthPath(path);
        return new RouteParser(routePath).reverse(tokenMap).toString();
    }

    /* PUBLIC ROUTES */
    public static loginCallback() {
        // Returns auth0 login callback path
        return '/login/callback';
    }

    public static userLogin(redirectTo?: string) {
        // Returns app route path to initial login view. If redirectTo argument is passed, append ?redirectTo= to URL
        const result = '/';

        const auth = new Auth();
        if (redirectTo) {
            auth.setRedirectToAfterLogin(redirectTo);
        } else {
            auth.clearRedirectToAfterLogin();
        }

        return result;
    }

    public static userLogout() {
        return '/logout';
    }

    /* AUTHENTICATED ROUTES */
    public static archivedChart(associationId: number, archivedOrderId: number) {
        // Returns app route path to association inventory detail view for specific inventoryId
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'reserveStudy', 'associationId', associationId);
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Reports.ArchivedChart, { associationId: associationId, archivedOrderId: archivedOrderId });
    }

    public static associationAdd() {
        // Returns app route path to add association view
        return NavigationLinks.composeAuthenticatedRoutePath('/association-add');
    }

    public static associationDetail(associationId: number) {
        // Returns app route path to association detail view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationDetail', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.Detail, { associationId: associationId });
    }

    public static associationPropertyReportPdf(associationId: number) {
        // Returns app route path to association property report pdf
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationPropertyReportPdf', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.PropertyReportPdf, { associationId: associationId });
    }

    public static associationClientQuestionsReportPdf(associationId: number) {
        // Returns app route path to association client quests report pdf
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationClientQuestionsReportPdf', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Reports.ClientQuestionsPdf, { associationId: associationId });
    }

    public static associationCaliforniaDisclosureReportPdf(associationId: number) {
        // Returns app route path to association california disclosure report pdf
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationCaliforniaDisclosureReportPdf', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Reports.CaliforniaDisclosurePdf, { associationId: associationId });
    }

    public static associationEdit(associationId: number) {
        // Returns app route path to association edit view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationEdit', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.Edit, { associationId: associationId });
    }

    public static associationViewOrder(associationId: number) {
        // Returns app route path to association order redirect
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationViewOrder', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.ViewOrder, { associationId: associationId });
    }

    public static associationInventory(associationId: number) {
        // Returns app route path to association inventory view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationInventory', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.Inventory, { associationId: associationId });
    }

    public static associationInventorySubgroup(associationId: number, subgroupCode: string) {
        // Returns app route path to association inventory subgroup view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationInventorySubgroup', 'associationId', associationId);
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationInventorySubgroup', 'subgroupCode', subgroupCode);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.InventorySubgroup, { associationId: associationId, subgroupCode: subgroupCode });
    }

    public static associationInventoryDetail(associationId: number, inventoryId: number) {
        // Returns app route path to association inventory detail view for specific inventoryId
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationInventoryDetail', 'associationId', associationId);
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationInventoryDetail', 'inventoryId', inventoryId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.InventoryDetail, { associationId: associationId, inventoryId: inventoryId });
    }

    public static reserveStudy(associationId: number) {
        // Returns app route path to association inventory detail view for specific inventoryId
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'reserveStudy', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.ReserveStudy, { associationId: associationId });
    }

    public static associationSearch() {
        // Returns app route path to association search view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.Search);
    }

    public static associationExportOrders() {
        // Returns app route path to association export orders search view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Association.ExportOrders);
    }

    public static clientDetail(clientId: number) {
        // Returns app route path to client detail view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'clientDetail', 'clientId', clientId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Client.Detail, { clientId: clientId });
    }

    public static clientEdit(clientId: number) {
        // Returns app route path to client edit view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'clientEdit', 'clientId', clientId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Client.Edit, { clientId: clientId });
    }

    public static clientSearch() {
        // Returns app route path to client search view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Client.Search);
    }

    public static clientSearchContacts() {
        // Returns app route path to client contacts search view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Client.SearchContacts);
    }

    public static clientAdd() {
        // Returns app route path to add client view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Client.Add);
    }

    // public static userAdd() {
    //     // Returns app route path to add user
    //     return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Invite.Add);
    // }

    public static inviteAdd(associationId: number = 0) {
        // Returns app route path to add client invite
        if (associationId) {
            return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Invite.AddForAssociation, { associationId: associationId });
        } else {
            return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Invite.Add);
        }
    }

    public static inviteDetail(inviteId: string) {
        // Returns app route path to add client invite
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Invite.Detail, { inviteId: inviteId });
    }

    public static userDashboard() {
        // Returns app route path to main dashboard landing page shown after login
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.HomeDashboard);
    }

    public static userPreparedBids() {
        // returns prepared bids view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Bids.PreparedBids);
    }

    public static clientDashboardHome() {
        // Returns app route path to main dashboard landing page shown after client login
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.ClientDashboard.Home);
    }

    public static userDetailObjId(userObjId: string) {
        // Returns app route path to user detail view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'userDetailObjId', 'userObjId', userObjId);
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Users.Detail, { userObjId: userObjId });
    }

    public static userEditObjId(userObjId: string) {
        // Returns app route path to user edit view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'userEdit', 'userObjId', userObjId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Users.Edit, { userObjId: userObjId });
    }

    public static userSearch() {
        // Returns app route path to user search view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Users.Search);
    }

    public static userAdd() {
        // Returns app route path to add user view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Users.Add);
    }

    public static userResumeAdd() {
        // Returns app route path to add user view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Users.AddResume);
    }

    public static userInventoryTemplateEditObjId(userInvTemplateObjId: string) {
        // Returns app route path to user inventory template edit view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'userInventoryTemplateEditObjId', 'userInventoryTemplateEditObjId', userInvTemplateObjId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.UserInventoryTemplates.Edit, { userInventoryTemplateObjId: userInvTemplateObjId });
    }

    public static myAccount(userObjId: string = '') {
        // Returns app route path to current user account profile detail view        
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Users.Detail, { userObjId: userObjId });
    }

    public static accountActivation(userObjId: string) {
        // Returns app route path to user activation
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'accountActivation', 'userObjId', userObjId);

        return NavigationLinks.composePublicRoutePath(NavigationLinks.AppAuthRoutePaths.AccountActivation.ActivateAccount, { userObjId: userObjId });
    }

    public static userChangePassword(userObjId: string) {
        // Returns app route path to user change password
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'userChangePassword', 'userObjId', userObjId);

        return NavigationLinks.composePublicRoutePath(NavigationLinks.AppAuthRoutePaths.ChangePassword.ChangePassword, { userObjId: userObjId });
    }

    public static orderAdd() {
        // Returns app route path to add order view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Order.Add);
    }

    public static orderAddBid() {
        // Returns app route path to add order bid view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Order.AddBid);
    }

    public static orderSearch() {
        // Returns app route path to order search view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Order.Search);
    }

    public static outstandingBids() {
        // Returns app route path to outstanding bids view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Bids.Outstanding);
    }

    public static reminderBids() {
        // Returns app route path to reminder bids view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Bids.Reminder);
    }

    public static researchBids() {
        // Returns app route path to research bids view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Bids.Research);
    }

    public static newResearchBids() {
        // Returns app route path to new research bids view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Bids.NewResearch);
    }

    // public static bidAdd() {
    //     // Returns app route path to add bid view
    //     return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Bids.Add);
    // }

    public static researchComponentsSearch() {
        // Returns app route path to components search view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.ResearchComponents.Search);
    }

    public static coordinatorJobsSearch() {
        // Returns app route path to coordinator jobs search view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Jobs.Coordinator);
    }

    public static wonJobs() {
        // Returns won jobs path
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Jobs.Won);
    }

    public static sendBids() {
        // Returns send bids path
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Bids.SendBids);
    }

    public static defaultManagers() {
        // Returns app route path to default managers view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Administration.DefaultManagers);
    }

    public static queryDatabase() {
        // Returns app route path to query database
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Administration.QueryDB);
    }

    public static lookupTables() {
        // Returns app route path to default lookup tables view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Administration.LookupTables);
    }

    public static lookupTableEdit(tableName: string) {
        // Returns app route path to lookup table edit view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Administration.LookupTableEdit, { tableName: tableName });
    }

    public static lookupTableEditChildObj(tableName: string, childObjId: string) {
        // Returns app route path to lookup table edit view
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Administration.LookupTableEditChildObj, { tableName: tableName, childObjId: childObjId });
    }

    public static regionEdit(regionId: number) {
        // Returns app route path to edit managers view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'regionEdit', 'regionId', regionId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Administration.RegionEdit, { regionId: regionId });
    }

    public static textContentEdit(textContentObjId: string) {
        // Returns app route path to edit text-content view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'textContentEdit', 'textContentObjId', textContentObjId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Administration.TextContentEdit, { textContentObjId: textContentObjId });
    }

    public static orderDetail(orderId: number) {
        // Returns app route path to order detail view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'orderDetail', 'orderId', orderId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Order.Detail, { orderId: orderId });
    }

    public static orderEdit(orderId: number) {
        // Returns app route path to order edit view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'orderEdit', 'orderId', orderId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Order.Edit, { orderId: orderId });
    }

    public static orderBidLetterPdf(orderId: number) {
        // Returns app route path to order bid letter pdf
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'orderBidLetterPdf', 'orderId', orderId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Order.BidLetterPdf, { orderId: orderId });
    }

    public static orderBidLetterMultiplePdf(orderObjIds: Array<string>) {
        // Returns app route path to order bid letter pdf for multiple orders

        const objIds = _.join(orderObjIds, ',');
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.PrintMulti.BidLetterMuliplePdf, { orderObjIds: objIds });
    }

    public static orderBidEnvelopePdf(orderId: number) {
        // Returns app route path to order bid envelope pdf
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'orderBidEnvelopePdf', 'orderId', orderId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Order.BidEnvelopePdf, { orderId: orderId });
    }

    public static orderBidEnvelopeMultiplePdf(orderObjIds: Array<string>) {
        // Returns app route path to order bid envelope pdf for multiple orders
        // Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'orderBidEnvelopeMultiplePdf', 'orderObjIds', orderObjIds);

        const objIds = _.join(orderObjIds, ',');
        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.PrintMulti.BidEnvelopeMuliplePdf, { orderObjIds: objIds });
    }

    public static componentDetail(componentObjId: string) {
        // Returns app route path to component detail view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'componentDetail', 'componentObjId', componentObjId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Component.Detail, { componentObjId: componentObjId });
    }

    public static componentEdit(componentObjId: string) {
        // Returns app route path to component edit view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'componentEdit', 'componentObjId', componentObjId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Component.Edit, { componentObjId: componentObjId });
    }

    public static associationAnalysis(associationId: number) {
        // Returns app route path to association analysis view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationAnalysis', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Reports.ReserveStudy, { associationId: associationId });
        // console.log('navigation.associationAnalysis routing to PortalReserveStudy');
        // return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Reports.PortalReserveStudy, { associationId: associationId });
    }

    public static portalAssociationReport(associationId: number) {
        // Returns app route path to association analysis view
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'portalAssociationReport', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Reports.PortalReserveStudy, { associationId: associationId });
    }

    public static associationReportPdf(associationId: number) {
        // Returns app route path to association report pdf
        Errors.ensureRequiredArgument(NavigationLinks.ModuleName, 'associationReportPdf', 'associationId', associationId);

        return NavigationLinks.composeAuthenticatedRoutePath(NavigationLinks.AppAuthRoutePaths.Reports.ReserveStudyPdf, { associationId: associationId });
    }
}

export interface ILinkProps {
    caption?: string;
    children?: any;
    className?: string;
}

export interface INavLinkProps extends ILinkProps {
    omitWrapper?: boolean;
    linkDetails?: any;
}

export interface IEmailLinkProps extends ILinkProps {
    email?: string;
    hideIcon?: boolean;
}

export class NavLinkComponent extends React.Component<INavLinkProps, any> {
    // Render a Link tag on the page
    public static propTypes = {
        caption: PropTypes.string,
        children: PropTypes.any,
        className: PropTypes.string,
        linkDetails: PropTypes.object,
        omitWrapper: PropTypes.bool,
    };

    public constructor(props: INavLinkProps) {
        super(props);
    }

    public render() {
        const { linkDetails, caption, className, children, omitWrapper, ...rest } = this.props;

        const linkCaption = _.toString(caption ? caption : '');

        let inner;
        if (_.get(linkDetails, 'to', '')) {
            inner = React.createElement(
                Link,
                _.cloneDeep(linkDetails),
                children ? children : linkCaption
            );
        } else {
            inner = linkCaption;
        }

        return (omitWrapper) ? inner : (<div className={`${className} inline-block`} title={linkCaption}>{inner}</div>);
    }
}

export class TodoLinkComponent extends React.Component<ILinkProps, any> {
    // Render a todo Link tag on the page that opens an alert
    public static propTypes = {
        caption: PropTypes.string,
        children: PropTypes.any,
        className: PropTypes.string,
    };

    public constructor(props: ILinkProps) {
        super(props);
    }

    public showAlert(message: string) {
        if (message) { alert(message); }
    }

    public render() {
        const { caption, className, children, ...rest } = this.props;

        const linkCaption = _.toString(caption ? caption : '');

        return (
            <a href="#" title={linkCaption} className={className} onClick={e => { this.showAlert('TODO: IMPLEMENT UI'); }}>{children}</a>
        );
    }
}

export class EmailLinkComponent extends React.Component<IEmailLinkProps, any> {
    // Render an a/href tag set to mailto
    public static propTypes = {
        caption: PropTypes.string,
        className: PropTypes.string,
        email: PropTypes.string,
        hideIcon: PropTypes.bool,
    };

    public constructor(props: IEmailLinkProps) {
        super(props);
    }

    public render() {
        const { caption, email, className, hideIcon, ...rest } = this.props;

        if (!email) { return (<div />); }

        const showIcon = (hideIcon) ? false : true;

        const linkCaption = _.toString(caption ? caption : '');

        return (
            <a href={`mailto:${email}`} title={linkCaption} className={className}>
                {(showIcon) ? <i className="fa fa-envelope" aria-hidden="true" /> : undefined} {email}
            </a>
        );
    }
}



// WEBPACK FOOTER //
// ./src/common/navigation.tsx